import { makeStyles } from "tss-react/mui";
import { ORIENTATION } from "@miplatform/constants";
import { MiTHEME } from "@miplatform/theme";
import { Box, Typography } from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * @typedef {Object} FieldsetItem
 * @property {Object} style
 * @property {string} label
 * @property {string} color
 * @property {string} icon
 * @property {function} valueGetter
 * @property {string} field
 */

/**
 * Renders a fieldset component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array.<FieldsetItem>} props.fieldset - The array of fieldset items.
 * @param {Object} props.row - The row object.
 * @param {boolean} props.showNulls - Whether to show null values.
 * @param {Object} props.rowStyle - The style object for the row.
 * @param {Object} props.containerStyle - The style object for the container.
 * @returns {JSX.Element} The rendered fieldset component.
 */
export const MiFieldSet = ({
  fieldset,
  row,
  containerStyle,
  showNulls = false,
  orientation = ORIENTATION.ROW,
  width = null,
}) => {
  const isRow = orientation === ORIENTATION.ROW;

  const { classes } = useStyles({ isRow, width });

  const renderFieldComponent = (item, index) => {
    const renderFieldValue = () => {
      if (item.valueGetter) {
        if (typeof item.valueGetter(row) === "string") {
          return (
            <Typography
              variant="body1"
              className={classes.valueTypography}
              lineHeight={1}
              numberOfLines={1}
            >
              {item.valueGetter(row)}
            </Typography>
          );
        }

        return item.valueGetter(row);
      }

      return (
        <Typography
          variant="body1"
          className={classes.valueTypography}
          lineHeight={1}
          numberOfLines={1}
        >
          {row ? row[item.field] : ""}
        </Typography>
      );
    };

    return (
      <Box
        key={index}
        className={
          item.column ? classes.fieldContainerColumn : classes.fieldContainerRow
        }
      >
        {item.icon && (
          <Box style={{ marginRight: 3 }}>
            <MiIcon
              path={item?.icon}
              size={12}
              color={item?.color || MiTHEME.GREY_MEDIUM_ALT}
            />
          </Box>
        )}
        {item.label && (
          <Typography
            className={classes.labelTypography}
            variant="body1"
            lineHeight={1.2}
            numberOfLines={1}
          >
            {`${item?.label}: `}
          </Typography>
        )}
        {renderFieldValue()}
      </Box>
    );
  };

  const renderField = (item, index) => {
    if (item?.component) {
      return <item.component key={`key_${index}`} />;
    }
    if (item.valueGetter || showNulls || (row && row[item.field])) {
      return (
        <Box key={`key_${index}`} style={item.style}>
          {renderFieldComponent(item, index)}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box className={classes.container} style={containerStyle}>
      {fieldset?.map((item, index) => {
        return (
          <Box key={item} className={classes.row}>
            {renderField(item, index)}
            {item.divider && (
              <Box className={classes.background}>
                <Box className={classes.divider} />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const useStyles = makeStyles()((theme, { isRow, width }) => ({
  container: {
    display: "flex",
    flexDirection: isRow ? "row" : "column",
    flexWrap: isRow ? "wrap" : "nowrap",
    gap: 20,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    width: width || "auto",
    maxWidth: width || "100%",
  },
  fieldContainerRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  fieldContainerColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 5,
  },
  background: {
    backgroundColor: theme.palette.grey.accent,
  },
  divider: {
    width: 1,
  },
  labelTypography: {
    fontSize: 14,
    color: theme.palette.grey?.main,
    fontWeight: "bold",
  },
  valueTypography: {
    fontSize: 14,
    color: theme.palette.grey?.dark,
  },
}));
